import { FLAG_USE_CONSOLE_LOGGER } from "../constants";

/*
JSON.stringify() doesn't try to solve cyclic object references problem and fails accordingly.
To serialize circular references, cyclic references are found and removed from stringify process.
(For reference: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Errors/Cyclic_object_value)
*/
function getCircularReplacer() {
    const seen = new WeakSet();

    return (key: string, value: unknown) => {
        if (typeof value === "object" && value !== null) {
            if (seen.has(value)) {
                return;
            }
            seen.add(value);
        } else if (typeof value === "bigint") {
            return value.toString();
        }
        return value;
    };
}

/**
 * A diffing approach implemented by using {@link JSON.stringify}. (Algorithm is order sensitive in terms of
 * object/array values) If an order insensitive implementation needed, then a more complex approach should be tried.
 */
function deepDiffer<T>(prev: T, next: T, onError?: (e: Error) => void): boolean {
    let result = prev === next;

    if (result) {
        return false;
    }

    try {
        result = JSON.stringify(prev) !== JSON.stringify(next);
    } catch (e) {
        // Which means a circular object or a BigInt value is tried to be stringified.
        if ((e as Error).name === "TypeError") {
            result = JSON.stringify(prev, getCircularReplacer()) !== JSON.stringify(next, getCircularReplacer());
        } else {
            FLAG_USE_CONSOLE_LOGGER && console.error(e);
            onError && onError(e as Error);
            result = true;
        }
    }

    return result;
}

function shallowDiffer<T>(a: T, b: T): boolean {
    for (const i in a) {
        if (!(i in b)) {
            return true;
        }
    }

    for (const i in b) {
        if (a[i] !== b[i]) {
            return true;
        }
    }

    return false;
}

export {
    shallowDiffer,
    deepDiffer
};
